/**
 * RichTextEditor — Tiptap-based WYSIWYG editor
 * Supports: Paragraph, H2, H3, Bold, Italic, Underline, Bullet list, Ordered list, Blockquote, Clear
 * Outputs and accepts HTML strings.
 */
import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Quote,
  Heading2,
  Heading3,
  Pilcrow,
  Minus,
} from 'lucide-react';

// ── Toolbar button ────────────────────────────────────────────────────────────
const ToolbarBtn = ({ onClick, active, title, children, disabled }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    disabled={disabled}
    title={title}
    className={`
      inline-flex items-center justify-center w-8 h-8 rounded-md text-sm transition-colors
      ${active
        ? 'bg-[#194a50] text-white shadow-sm'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
      ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
    `}
  >
    {children}
  </button>
);

// ── Toolbar separator ─────────────────────────────────────────────────────────
const Sep = () => <div className="w-px h-5 bg-gray-200 mx-0.5 self-center" />;

// ── Block-type select (Paragraph / H2 / H3) ───────────────────────────────────
const BlockSelect = ({ editor }) => {
  const getLabel = () => {
    if (editor.isActive('heading', { level: 2 })) return 'H2';
    if (editor.isActive('heading', { level: 3 })) return 'H3';
    if (editor.isActive('heading', { level: 4 })) return 'H4';
    return 'P';
  };

  const options = [
    { label: 'Paragraph', value: 'paragraph', action: () => editor.chain().focus().setParagraph().run() },
    { label: 'Heading 2', value: 'h2',        action: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { label: 'Heading 3', value: 'h3',        action: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
    { label: 'Heading 4', value: 'h4',        action: () => editor.chain().focus().toggleHeading({ level: 4 }).run() },
  ];

  return (
    <select
      value={getLabel()}
      onChange={(e) => {
        const opt = options.find((o) => o.label.replace('Heading ', 'H').replace('Paragraph', 'P') === e.target.value || o.label === e.target.value);
        if (opt) opt.action();
      }}
      onMouseDown={(e) => e.stopPropagation()}
      className="h-8 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md px-2 pr-6 appearance-none cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#194a50]"
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 4px center', backgroundSize: '14px' }}
    >
      <option value="P">Paragraph</option>
      <option value="H2">Heading 2</option>
      <option value="H3">Heading 3</option>
      <option value="H4">Heading 4</option>
    </select>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Write a description…',
  minHeight = 220,
  testId,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
      }),
      Underline,
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'outline-none prose prose-sm max-w-none px-4 py-3 focus:outline-none',
        style: `min-height:${minHeight}px`,
        ...(testId ? { 'data-testid': testId } : {}),
      },
    },
    onUpdate: ({ editor }) => {
      if (onChange) onChange(editor.getHTML());
    },
  });

  // Sync external value changes (e.g. when a different property is loaded)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="rounded-xl border border-gray-200 focus-within:border-[#194a50] focus-within:ring-2 focus-within:ring-[#194a50]/10 transition-all overflow-hidden bg-white">

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50/80">
        {/* Block type */}
        <BlockSelect editor={editor} />

        <Sep />

        {/* Inline marks */}
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>

        <Sep />

        {/* Lists */}
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet list"
        >
          <List className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered list"
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarBtn>

        <Sep />

        {/* Blockquote */}
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Blockquote"
        >
          <Quote className="w-3.5 h-3.5" />
        </ToolbarBtn>

        {/* Horizontal rule */}
        <ToolbarBtn
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          active={false}
          title="Horizontal rule"
        >
          <Minus className="w-3.5 h-3.5" />
        </ToolbarBtn>
      </div>

      {/* Editor area */}
      <EditorContent editor={editor} />

      {/* Prose styles injected via a style tag so we don't need @tailwindcss/typography */}
      <style>{`
        .tiptap p { margin: 0 0 0.6em 0; font-size: 0.875rem; color: #374151; line-height: 1.6; }
        .tiptap p:last-child { margin-bottom: 0; }
        .tiptap h2 { font-size: 1.125rem; font-weight: 700; color: #111827; margin: 0.8em 0 0.3em 0; }
        .tiptap h3 { font-size: 1rem;    font-weight: 700; color: #111827; margin: 0.7em 0 0.25em 0; }
        .tiptap h4 { font-size: 0.875rem;font-weight: 700; color: #374151; margin: 0.6em 0 0.2em 0; }
        .tiptap ul { list-style: disc;   padding-left: 1.4em; margin: 0 0 0.6em 0; font-size: 0.875rem; color: #374151; }
        .tiptap ol { list-style: decimal;padding-left: 1.4em; margin: 0 0 0.6em 0; font-size: 0.875rem; color: #374151; }
        .tiptap li { margin-bottom: 0.2em; }
        .tiptap blockquote { border-left: 3px solid #d1d5db; padding-left: 1em; color: #6b7280; font-style: italic; margin: 0.5em 0; }
        .tiptap hr { border: none; border-top: 1px solid #e5e7eb; margin: 0.8em 0; }
        .tiptap strong { font-weight: 700; }
        .tiptap em     { font-style: italic; }
        .tiptap u      { text-decoration: underline; }
        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #d1d5db;
          pointer-events: none;
          float: left;
          height: 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
